import { useState, useEffect, useContext, useMemo } from "react";
import UserContext from "../../../context/UserContext";
import ProductApi from "../../../api/ProductApi";
import Payment from "../../../util/Payment";
import { loginAlert } from "../../common/CustomAlert";

const MEMBERSHIP_PROD_TYPE = "PRODT04";

const BADGE_IMAGES = {
    bestSelling: "/static/img/assets/membership_label_02.png",
    bestOptions: "/static/img/assets/membership_label_03.png",
};

const calcDiscountedPrice = (price, disc = 0) => {
    const raw = Number(price) * (100 - Number(disc || 0)) * 0.01;
    return Math.floor(raw / 100) * 100;
};

const getOptionMeta = (product) => {
    if (product.prod_adv === "bestOptions") {
        return {
            variant: "membership-option-item--benefit",
            badge: BADGE_IMAGES.bestOptions,
        };
    }
    if (product.prod_adv === "bestSelling") {
        return {
            variant: "membership-option-item--popular",
            badge: BADGE_IMAGES.bestSelling,
        };
    }
    return { variant: "", badge: null };
};

const formatBonusLabel = (product) => {
    const match = String(product.prod_title || "").match(/\+(\d+)개/);
    return match ? `+${match[1]}개` : "";
};

const MembershipRequestView = () => {
    const { user, updateUserInfo } = useContext(UserContext);
    const [products, setProducts] = useState([]);
    const [selectedProdId, setSelectedProdId] = useState(null);
    const [loading, setLoading] = useState(true);

    const sortedProducts = useMemo(
        () => [...products].sort((a, b) => Number(a.prod_price) - Number(b.prod_price)),
        [products]
    );

    const selectedProduct = useMemo(
        () => sortedProducts.find((p) => p.prod_id === selectedProdId) || null,
        [sortedProducts, selectedProdId]
    );

    useEffect(() => {
        ProductApi.getMembershipOfReadProduct()
            .then((list) => {
                const membershipProducts = (list || []).filter(
                    (p) => p.prod_type === MEMBERSHIP_PROD_TYPE
                );
                const sorted = [...membershipProducts].sort(
                    (a, b) => Number(a.prod_price) - Number(b.prod_price)
                );
                setProducts(sorted);
                if (sorted.length > 0) {
                    const defaultProduct =
                        sorted.find((p) => p.prod_adv === "bestSelling") || sorted[0];
                    setSelectedProdId(defaultProduct.prod_id);
                }
            })
            .catch((err) => {
                console.error("의뢰열람 이용권 상품 조회 실패:", err);
            })
            .finally(() => setLoading(false));
    }, []);

    const handlePayBtn = () => {
        if (!user?.userId) return loginAlert("로그인이 필요합니다.");
        if (!selectedProduct) return alert("이용권 상품을 선택해 주세요.");

        const payUser = {
            userEmail: user.userEmail,
            userNm: user.userNm,
            userPhone: user.userPhone,
        };

        const data = {
            prodId: String(selectedProduct.prod_id),
            prodTitle: `의뢰열람 ${selectedProduct.prod_title}`,
            prodDisc: 0,
            prodPrice: calcDiscountedPrice(
                selectedProduct.prod_price,
                selectedProduct.prod_disc
            ),
        };

        Payment.openPaymentPage(data, payUser, "/order/membership/complete", () => {
            updateUserInfo();
        });
    };

    return (
        <section className="membership-section membership-option">
            <div className="membership-banner">
                <div className="membership-banner-content">
                    <div className="membership-banner-text">
                        <h2 className="membership-banner-title">의뢰열람 이용권</h2>
                        <p className="membership-banner-description">
                            의뢰 알림 열람에 사용할 이용권을 구매할 수 있습니다.
                            구매한 이용권은 누적되어 보유 수량에 합산됩니다.
                        </p>
                    </div>
                    <div className="membership-banner-visual" aria-hidden="true">
                        <img src="/static/img/assets/banner_membership.png" alt="" />
                    </div>
                </div>
            </div>

            <div className="membership-option-section">
                <h3 className="membership-option-title">의뢰 알림 이용권</h3>
                {loading && <p>상품을 불러오는 중입니다.</p>}
                {!loading && sortedProducts.length === 0 && (
                    <p>등록된 이용권 상품이 없습니다.</p>
                )}
                <div className="membership-option-grid">
                    {sortedProducts.map((product) => {
                        const meta = getOptionMeta(product);
                        const discountedPrice = calcDiscountedPrice(
                            product.prod_price,
                            product.prod_disc
                        );
                        const showOriginPrice = Number(product.prod_disc) > 0;
                        const bonus = formatBonusLabel(product);

                        return (
                            <label
                                key={product.prod_id}
                                className={`membership-option-item${meta.variant ? ` ${meta.variant}` : ""}`}
                            >
                                <input
                                    type="radio"
                                    name="ticket-count"
                                    value={product.prod_id}
                                    checked={selectedProdId === product.prod_id}
                                    onChange={() => setSelectedProdId(product.prod_id)}
                                />
                                {meta.badge && (
                                    <span className="membership-card-badge">
                                        <img src={meta.badge} alt="" />
                                    </span>
                                )}
                                <span className="membership-option-box">
                                    <span>
                                        {product.prod_title}
                                        {bonus && <em>{bonus}</em>}
                                    </span>
                                    <strong>
                                        {showOriginPrice && (
                                            <del>{Number(product.prod_price).toLocaleString()}원</del>
                                        )}
                                        {discountedPrice.toLocaleString()}원
                                    </strong>
                                </span>
                            </label>
                        );
                    })}
                </div>
            </div>

            <div className="btn_area">
                <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handlePayBtn}
                    disabled={!selectedProduct}
                >
                    <span>결제하기</span>
                </button>
            </div>
        </section>
    );
};

export default MembershipRequestView;
